import ArticleMeta from "@dogstrust/dt-web-core-lib-front-end/src/components/ArticleMeta";
import SocialSharing from "@dogstrust/dt-web-core-lib-front-end/src/components/common/SocialSharing/SocialSharing.ie";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import WrappedSection from "@dogstrust/dt-web-core-lib-front-end/src/containers/WrappedSection/WrappedSection";
import IntroToDisplay from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import type { IntroType } from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import { ParagraphSections } from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import ParagraphTextArea from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphTextArea/ParagraphTextArea";
import SectionArticleOrEventCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionArticleOrEventCards";
import SectionHelpAdviceNewsletterPromo from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionHelpAdviceNewsletterPromo";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/ie/components/Layout/Layout";

import { Colours } from "@dogstrust/dt-web-core-lib-front-end/src/utils/colours";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { mapContentUnionToSignPostCardProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { graphql } from "gatsby";
import React from "react";
import { useEffect } from "react";
import * as styles from "./ArticlePage.module.scss";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head.ie";
export const Head: React.FC<Props> = ({
	data: {
		nodeArticle: {
			title,
			path,
			created,
			field_hreflang_url,
			field_short_description,
			field_meta_tags,
			relationships: { field_featured_image },
		},
	},
}) => {
	const schema = {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: title,
		description: field_short_description,
		image: `${getImagePath(
			"400x300",
			field_featured_image?.field_media_image?.uri,
		)}`,
		datePublished: created,
		author: {
			"@type": "Organization",
			name: "Dogs Trust",
			url: process.env.GATSBY_DOMAIN_NAME,
		},
	};
	return (
		<DefaultHead pageUrl={path.alias} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `${title} | Dogs Trust`,
					description: field_short_description,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_title: `${title} | Dogs Trust`,
					og_description: field_short_description,
					og_image_url: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: field_short_description,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					schemaMarkup: schema,
				})}
			/>
		</DefaultHead>
	);
};

interface Props {
	data: Queries.ArticlePageQuery;
}

const ArticlePage: React.FC<Props> = ({
	data: {
		nodeArticle: {
			title,
			path,
			field_meta_tags,
			breadcrumbs,
			field_is_vet_friendly_clinic,
			field_author,
			field_short_description,
			field_body,
			field_show_newsletter_promo,
			relatedTopicLinks,
			relationships: {
				field_page_sections,
				field_featured_image,
				field_related_help_advice_articl,
			},
		},
		newsletterPromo,
	},
}) => {
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");
		return () => deregisterPage();
	}, []);

	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<div className={styles.contentarea}>
				<IntroToDisplay
					key={0}
					introType={
						field_is_vet_friendly_clinic
							? ("Intro-Hero-With-Image-Teal" as IntroType)
							: ("Intro-Hero-With-Image" as IntroType)
					}
					title={title}
					body={
						!!field_short_description && `<p>${field_short_description}</p>`
					}
					imgSrc={field_featured_image?.field_media_image?.uri}
					imgAlt={field_featured_image?.field_media_image?.alt}
					firstSection={
						{
							__typename: "paragraph__textarea",
						} as Queries.ParagraphSectionsDataFragment
					}
				/>
				{field_body?.value?.length > 0 ? (
					<ParagraphTextArea htmlText={field_body?.value} />
				) : null}
				{field_page_sections.map((section, i) => (
					<ParagraphSections
						key={"id" in section ? section.id : i}
						data={section}
						thisSectionKey={i}
					/>
				))}
				<WrappedSection
					sectionName="ArticlePageSocialMedia"
					sectionColour={Colours.WHITE}
				>
					<SocialSharing
						title="If you liked this page, tell the world!"
						shareBodyText="I saw this on the Dogs Trust’s website and thought of you"
						sharePath={
							field_meta_tags?.canonical_url
								? field_meta_tags?.canonical_url
								: process.env.GATSBY_DOMAIN_NAME + path.alias
						}
					/>
				</WrappedSection>
				{relatedTopicLinks.length > 0 && (
					<div className={styles.metawrapper}>
						<ArticleMeta
							author={field_author}
							tags={relatedTopicLinks.map((topic) => ({
								title: topic.title,
								url: topic.uri,
							}))}
						/>
					</div>
				)}
				{field_related_help_advice_articl.length > 0 ? (
					<SectionArticleOrEventCards
						items={(
							field_related_help_advice_articl as Queries.ContentUnionDataFragment[]
						).map(mapContentUnionToSignPostCardProps)}
						title="Related articles"
						hideFromJumpLinks={true}
					/>
				) : null}
				{field_show_newsletter_promo && (
					<SectionHelpAdviceNewsletterPromo
						title={newsletterPromo.field_front_end_title}
						body={newsletterPromo.field_section_short_description.value}
						buttonText={newsletterPromo.field_link.title}
						buttonUrl={newsletterPromo.field_link.uri}
						jumpLinksTitle={newsletterPromo.field_jump_links_title}
					/>
				)}
			</div>
		</Layout>
	);
};
export default ArticlePage;

export const query = graphql`
    query ArticlePage($path: String) {
        nodeArticle(path: { alias: { eq: $path } }) {
            ...Article
        }
        newsletterPromo: nodeSection(
            title: { eq: "Section-Newsletter-Promo" }
        ) {
            field_front_end_title
			field_jump_links_title
            field_link {
                title
                uri
            }
            field_section_short_description {
                value
            }
        }
    }
`;
export const fragment = graphql`
    fragment Article on node__article {
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_body {
            value
        }
        created
		field_hreflang_url
        field_author
        field_is_vet_friendly_clinic
        field_short_description
        field_short_link_text
        field_node_weight
        field_show_newsletter_promo
        field_meta_tags {
            ...MetaTags
        }
        relatedTopicLinks {
            title
            uri
        }
        relationships {
            field_page_sections {
                ...ParagraphSectionsData
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_related_help_advice_articl {
                ...ContentUnionData
            }
            field_category_type {
                name
            }
            field_dog_advice_article_tags {
                name
            }
        }
    }

    fragment ArticleSignPost on node__article {
        title
        path {
            alias
        }
        field_body {
            value
        }
        created
        field_author
        field_is_vet_friendly_clinic
        field_short_description
        field_short_link_text
        field_node_weight
        relatedTopicLinks {
            title
            uri
        }
        relationships {
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }
`;
